import {matchPath} from 'react-router-dom';
import sdk from '@/sdk';
import * as store from '@/store';
import {BOOK_TYPE, DOC_TYPE} from '@/constants/document/doc';
import routers, {buildPath, ServerConfig} from '@/config/routers';
import {addParamToCustomUrl} from '@/utils/kuQuery';
import KU_QUERY from '@/constants/common/searchParamKeys';

import './event';
import {MessageType} from './constants';
import kuJSSDKEventBus, {INIT_READY, OPEN_DOC_READY} from './bus';

let isInit = false;

const kuJSSDKSessionStorage = {
    prefix: 'kuJSSDK-',
    setItem(key: string, data: any = null) {
        sessionStorage.setItem(`${this.prefix}${key}`, JSON.stringify(data));
    },
    getItem(key: string) {
        return JSON.parse(sessionStorage.getItem(`${this.prefix}${key}`) || 'null');
    },
    removeItem(key: string) {
        sessionStorage.removeItem(`${this.prefix}${key}`);
    },
};

function response(id: string, data: {
    data?: any;
    fail?: boolean;
    msg?: string;
} = {}) {
    if (window.top === window.self) {
        return;
    }
    window.parent.postMessage({
        type: 'kujssdk-message-response',
        id,
        data,
    }, '*');
}

const onMessageMap = {
    [MessageType.Init]: (id: string) => {
        if (isInit) {
            response(id);
            return;
        }
        kuJSSDKSessionStorage.setItem(MessageType.Init, id);
    },
    [MessageType.OpenDoc]: (id: string, data: {
        url: string;
        isEdit: boolean;
    }) => {
        let {url} = data;
        const {isEdit} = data;
        if (url) {
            kuJSSDKSessionStorage.setItem(MessageType.OpenDoc, id);
            if (isEdit) {
                const urlObj = addParamToCustomUrl(url, KU_QUERY.document.isNewDoc, 'true');
                url = urlObj.href;
            }
            location.href = url;
        }
    },
    [MessageType.CreateDoc]: async (
        id: string,
        data: {
            name?: string;
            parentGuid?: string;
        } = {},
        isBook = false
    ) => {
        const repository = store.getCurRepository() || sdk.as.userRepository;
        if (!repository) {
            return;
        }
        const {spaceGuid, groupGuid, repositoryGuid} = repository;
        const ksServer = await sdk.getKsServer();
        ksServer.createDocumentDirectory({
            spaceGuid,
            groupGuid,
            repositoryGuid,
            position: Date.now(),
            name: data.name || '',
            parentGuid: data.parentGuid || '',
            bookType: isBook ? BOOK_TYPE.IS_BOOK : BOOK_TYPE.NO_BOOK,
        } as any).then((result: WizDocument) => {
            const {docGuid} = result;
            const path = buildPath(routers.Knowledge.path, {
                spaceGuid,
                groupGuid,
                repositoryGuid,
                docGuid,
            });
            response(id, {
                data: {
                    url: `${ServerConfig.origin}${path}`,
                    spaceGuid,
                    groupGuid,
                    repositoryGuid,
                    docGuid,
                },
            });
        }).catch((err) => {
            response(id, {
                fail: true,
                msg: err.toString(),
            });
        });
    },
    [MessageType.CreateDir]: (
        id: string,
        data: {
            name?: string;
            parentGuid?: string;
        } = {}
    ) => {
        onMessageMap[MessageType.CreateDoc](id, data, true);
    },
    [MessageType.CopyDoc]: async (id: string, data: {
        docGuid: string;
        name?: string;
    }) => {
        const {docGuid, name = ''} = data;
        const repository = store.getCurRepository() || sdk.as.userRepository;
        if (!repository) {
            return;
        }
        const {spaceGuid, groupGuid, repositoryGuid} = repository;
        const ksServer = await sdk.getKsServer();
        await ksServer.newCopyDoc(
            docGuid,
            DOC_TYPE.DOCUMENT,
            {
                name,
                docGuid,
                withChild: false,
            } as any
        ).then((result: any) => {
            const {docGuid} = result.docs[0];
            const path = buildPath(routers.Knowledge.path, {
                spaceGuid,
                groupGuid,
                repositoryGuid,
                docGuid,
            });
            response(id, {
                data: {
                    url: `${ServerConfig.origin}${path}`,
                    spaceGuid,
                    groupGuid,
                    repositoryGuid,
                    docGuid,
                },
            });
        }).catch((err) => {
            response(id, {
                fail: true,
                msg: err.toString(),
            });
        });
    },
    [MessageType.GetPageInfo]: async (id: string) => {
        const match = matchPath<{
            spaceGuid: string;
            groupGuid: string;
            repositoryGuid: string;
            docGuid: string;
        }>(location.pathname, {path: routers.Knowledge.docPath});
        let docInfo = {};
        if (match) {
            docInfo = {
                spaceGuid: match.params.spaceGuid,
                groupGuid: match.params.groupGuid,
                repositoryGuid: match.params.repositoryGuid,
                docGuid: match.params.docGuid,
            };
        }

        response(id, {
            data: {
                url: location.href,
                ...docInfo,
            },
        });
    },
};

window.addEventListener('message', async (e) => {
    const {
        id,
        type,
        data,
    }: {
        id: string;
        type: MessageType;
        data: any;
    } = e.data || {};
    const onMessage = onMessageMap[type];
    if (typeof id !== 'string' || !type || !id.startsWith?.('kujssdk') || !onMessage) {
        return;
    }

    onMessage(id, data);
});

kuJSSDKEventBus.on(INIT_READY, () => {
    isInit = true;
    const messageId = kuJSSDKSessionStorage.getItem(MessageType.Init);
    if (messageId) {
        response(messageId);
        kuJSSDKSessionStorage.removeItem(MessageType.Init);
    }
});

kuJSSDKEventBus.on(OPEN_DOC_READY, () => {
    const messageId = kuJSSDKSessionStorage.getItem(MessageType.OpenDoc);
    if (messageId) {
        response(messageId);
        kuJSSDKSessionStorage.removeItem(MessageType.OpenDoc);
    }
});
